import styled from "styled-components";
import { LabelAndValue, Logo, Subtitle, Title } from "../../components";
import { WidgetThemeStyles } from "../../domain_objects/CrossSellSetupResponse";
import { useTranslation } from "react-i18next";
import SelectedVenueDetailsHelper from "../../helpers/SelectedVenueDetailsHelper";
import { State } from "../../types";
import DateHelper from "../../helpers/DateHelper";

const StyledPageContainer = styled.div`
  display: grid;
  row-gap: 16px;
`;

const StyledHeader = styled.div`
  justify-content: center;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  flex-grow: 1;
`;

const StyledSection = styled.div`
  display: grid;
  row-gap: 8px;
`;

const StyledValuesContainer = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-auto-rows: minmax(36px, auto);
  padding: 0 8px;
`;

export type BookingConfirmationPageProps = {
  state: State;
  styles: WidgetThemeStyles;
};

export const BookingConfirmationPage: React.FC<
  BookingConfirmationPageProps
> = ({ state, styles }) => {
  const { t } = useTranslation();

  const data = {
    Reference: state.bookingConfirmationDetails!.Booking.Reference,
    Location: SelectedVenueDetailsHelper.getVenueDetails(state).location!,
    VenueName: SelectedVenueDetailsHelper.getVenueDetails(state).name!,
    Date: state.bookingDetails.selectedDate.toDateString(),
    Time: DateHelper.sliceTimeSlot(state.bookingDetails.timeSlot!),
    People: state.bookingDetails.partySize.toString(),
    Name:
      `${state.contactDetails!.Title ? `${state.contactDetails!.Title} ` : ""}` +
      state.contactDetails!.FirstName +
      " " +
      state.contactDetails!.Surname,
    Mobile: state.contactDetails!.Mobile,
    Email: state.contactDetails!.Email,
    ...(!!state.contactDetails!.MembershipId && {
      MembershipId: state.contactDetails!.MembershipId as string,
    }),
  };

  return (
    <StyledPageContainer>
      <Logo styles={styles} />
      <StyledHeader>
        <StyledTitle styles={styles}>
          {t("ThankYouForYourReservationAtVenue", {
            venueName: data.VenueName,
          })}
        </StyledTitle>
      </StyledHeader>
      <StyledSection>
        <Subtitle styles={styles} dataId="booking-details-title">
          <strong>{t("BookingDetails")}</strong>
        </Subtitle>
        <StyledValuesContainer>
          <LabelAndValue
            fieldName={t("Reference")}
            styles={styles}
            dataId="booking-reference"
          >
            {data.Reference}
          </LabelAndValue>
          <LabelAndValue
            fieldName={t("Location")}
            styles={styles}
            dataId="location"
          >
            <>
              <span style={{ fontWeight: 700 }}>{data.VenueName}</span>
              <br />
              {SelectedVenueDetailsHelper.formatVenueAddressIntoLines(
                data.Location
              )}
            </>
          </LabelAndValue>
          <LabelAndValue fieldName={t("Date")} styles={styles} dataId="date">
            {data.Date}
          </LabelAndValue>
          <LabelAndValue fieldName={t("Time")} styles={styles} dataId="time">
            {data.Time}
          </LabelAndValue>
          <LabelAndValue
            fieldName={t("People")}
            styles={styles}
            dataId="people"
          >
            {data.People}
          </LabelAndValue>
        </StyledValuesContainer>
      </StyledSection>
      <StyledSection>
        <Subtitle styles={styles} dataId="contact-details-title">
          <strong>{t("ContactDetails")}</strong>
        </Subtitle>
        <StyledValuesContainer>
          <LabelAndValue fieldName={t("Name")} styles={styles} dataId="name">
            {data.Name}
          </LabelAndValue>
          <LabelAndValue
            fieldName={t("MobileNumber")}
            styles={styles}
            dataId="mobile-number"
          >
            {data.Mobile}
          </LabelAndValue>
          <LabelAndValue fieldName={t("Email")} styles={styles} dataId="email">
            {data.Email}
          </LabelAndValue>
          {data.MembershipId && (
            <LabelAndValue
              fieldName={
                state.crossSellSetup?.ThemeSettings?.MembershipIdTitle ??
                t("MembershipId")
              }
              styles={styles}
              dataId="membership-id"
            >
              {data.MembershipId}
            </LabelAndValue>
          )}
        </StyledValuesContainer>
      </StyledSection>
    </StyledPageContainer>
  );
};

export default BookingConfirmationPage;
